import { useCallback, useMemo } from 'react';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { useUserSuggestions } from '@confluence/share-and-restrict-dialog/entry-points/useUserSuggestions.ts';
import { ShareAndRestrictDialogController } from '@confluence/share-and-restrict-dialog/entry-points/ShareAndRestrictDialogController.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { UserPickerUserPrincipal } from '@confluence/share-and-restrict-dialog/entry-points/commonTypes.ts';

export const useShareLinkSocialOnPublish = (
	contentId: string,
	spaceKey: string,
	userId: string | null,
): {
	isEligible: boolean;
	suggestedUsers: UserPickerUserPrincipal[];
	showShareModal: () => void;
} => {
	const userIdArray = useMemo(() => (userId ? [userId] : []), [userId]);

	const { cloudId, orgId } = useSessionData();

	const { suggestedUsers, loading } = useUserSuggestions({
		currentPickedPrincipalIds: userIdArray,
		maxDisplayResults: 3,
		orgId,
		siteId: cloudId,
	});

	const { showModal } = useDialogs();

	const showShareModal = useCallback((): void => {
		showModal(ShareAndRestrictDialogController, {
			contentId,
			contentType: 'page',
			allowRestrictions: true,
			pageModes: 'view',
			refetchButtonData: () => {},
			isUnpublishedDraft: false,
			spaceKey,
			defaultSuggestedUsers: suggestedUsers || [],
		});
	}, [showModal, contentId, spaceKey, suggestedUsers]);

	const isEligible = useMemo(() => {
		return !loading && suggestedUsers !== null && suggestedUsers.length > 0;
	}, [suggestedUsers, loading]);

	const memoizedSuggestedUsers = useMemo(() => {
		return suggestedUsers || [];
	}, [suggestedUsers]);

	return {
		isEligible,
		suggestedUsers: memoizedSuggestedUsers,
		showShareModal,
	};
};
